import { parseExtraSections } from "./detailSections";
import { compareNatural, stripDiacritics, toText } from "./text";
import type { ExtraSection, LocalizedLabel, SourceRow } from "./types";

export const SYSTEM_STATUS = {
  fulfilled: "fulfilled",
  almost_fulfilled: "almost_fulfilled",
  partially_fulfilled: "partially_fulfilled",
  only_limited: "only_limited",
  not_fulfilled: "not_fulfilled",
  insufficient_information: "insufficient_information",
  zero: "zero",
  unknown: "unknown",
} as const;
export type SystemStatus = (typeof SYSTEM_STATUS)[keyof typeof SYSTEM_STATUS];

/** Higher = worse. Aggregates (domain, "all locations") use the worst status. */
const STATUS_PRIORITY: Record<SystemStatus, number> = {
  not_fulfilled: 6,
  only_limited: 5,
  partially_fulfilled: 4,
  almost_fulfilled: 3,
  insufficient_information: 2,
  fulfilled: 1,
  zero: 0,
  unknown: -1,
};

/** The 0–6 criterion rating scale (`Criterion_score_<n>` columns). */
export const SYSTEM_SCORE_SCALE: { number: string; status: SystemStatus }[] = [
  { number: "0", status: "zero" },
  { number: "1", status: "fulfilled" },
  { number: "2", status: "almost_fulfilled" },
  { number: "3", status: "partially_fulfilled" },
  { number: "4", status: "only_limited" },
  { number: "5", status: "not_fulfilled" },
  { number: "6", status: "insufficient_information" },
];

/** Statuses in the order they are listed in summaries and legends. */
export const SYSTEM_STATUS_DISPLAY_ORDER: SystemStatus[] = [
  "fulfilled",
  "almost_fulfilled",
  "partially_fulfilled",
  "only_limited",
  "not_fulfilled",
  "insufficient_information",
  "zero",
];

export const SYSTEM_ANALYSIS_WORKSHEETS = {
  criterion: "Annex4_Criterion",
  data: "Annex4_Data",
} as const;
export const ALL_LOCATIONS_ID = "all";

export type ScoreTexts = Record<string, string>;

export interface SystemCriterion {
  id: string;
  titleNo: string;
  titleEn: string;
  criterionNo: string;
  criterionEn: string;
  assessmentNo: string;
  assessmentEn: string;
  evidenceNo: string;
  evidenceEn: string;
  status: SystemStatus;
  /** Distinct statuses across locations (for the "all locations" view). */
  statuses?: SystemStatus[];
  sources: { labelNo: string; labelEn: string; url: string }[];
  extraSections?: ExtraSection[];
  scoreTextsNo?: ScoreTexts;
  scoreTextsEn?: ScoreTexts;
  workingCommentNo: string;
  workingCommentEn: string;
  metadata: Record<string, string>;
}

export interface SystemDomain {
  id: string;
  titleNo: string;
  titleEn: string;
  descriptionNo?: string;
  descriptionEn?: string;
  items: SystemCriterion[];
}

export interface SystemLocation {
  locationId: string;
  locationName: LocalizedLabel;
}

export interface SystemAnalysisAudit {
  updatedAt: string;
  updatedBy: string | null;
}

export interface SystemAssessmentRow {
  criterion_id: string;
  row_data: SourceRow;
}

export interface SystemAnalysis {
  analysisId: string;
  analysisName: LocalizedLabel;
  year: number | string;
  aboutTitle?: LocalizedLabel;
  aboutText?: LocalizedLabel;
  locations: SystemLocation[];
  domainsByLocation: Record<string, SystemDomain[]>;
  auditByLocation?: Record<string, SystemAnalysisAudit>;
  assessmentsByLocation?: Record<string, SystemAssessmentRow[]>;
}

/** The criteria of one year that has criteria of its own (not copied). */
export interface SystemCriteriaSet {
  year: string;
  criteria: SystemCriterion[];
}

export interface SystemAnalysisDataset {
  analyses: SystemAnalysis[];
  /** Years with criteria of their own, newest first. */
  criteriaSets?: SystemCriteriaSet[];
  /**
   * For every year with year-specific criteria: the year they come from,
   * i.e. the year itself or, for copied criteria, the original year.
   */
  criteriaSourceByYear?: Record<string, string>;
}

/**
 * Column on criteria rows copied to a year without criteria of its own (see
 * save_system_analysis_assessments): the year they were copied from.
 */
export const CRITERIA_FROM_YEAR_COLUMN = "Criteria_from_year";

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

/**
 * Accepts a status name or a score ("3", "3 - Delvis"). Anything else that
 * is not literally "unknown" counts as insufficient information.
 */
export function normalizeSystemStatus(value: unknown): SystemStatus {
  const raw = toText(value);
  const score = raw.match(/^([0-6])(?:\s|[-:.)]|$)/)?.[1];
  if (score) return SYSTEM_SCORE_SCALE[Number(score)]?.status ?? "unknown";
  return (Object.values(SYSTEM_STATUS) as string[]).includes(raw)
    ? (raw as SystemStatus)
    : "insufficient_information";
}

export function pickWorstStatus(statuses: SystemStatus[]): SystemStatus {
  return statuses
    .map(normalizeSystemStatus)
    .reduce<SystemStatus>(
      (worst, status) => (STATUS_PRIORITY[status] > STATUS_PRIORITY[worst] ? status : worst),
      "unknown",
    );
}

export function getScoreNumberForStatus(status: SystemStatus): string {
  return SYSTEM_SCORE_SCALE.find((entry) => entry.status === status)?.number ?? "";
}

/** Maps an assessment cell ("3 - Delvis oppfylt", "partially met", …) to a status. */
export function mapAssessmentToStatus(rawValue: unknown): SystemStatus {
  const raw = toText(rawValue);
  if (!raw) return "unknown";
  const number = raw.match(/^(\d+)/)?.[1];
  const scored = SYSTEM_SCORE_SCALE.find((entry) => entry.number === number);
  if (scored) return scored.status;

  const text = raw.toLowerCase();
  const rules: [string[], SystemStatus][] = [
    [["nesten helt oppfylt"], "almost_fulfilled"],
    [["helt oppfylt", "fullt oppfylt"], "fulfilled"],
    [["delvis oppfylt"], "partially_fulfilled"],
    [["i liten grad oppfylt"], "only_limited"],
    [["ikke oppfylt"], "not_fulfilled"],
    [["ikke nok informasjon", "not enough", "insufficient"], "insufficient_information"],
    [["not at all"], "not_fulfilled"],
    [["only limited"], "only_limited"],
    [["almost"], "almost_fulfilled"],
    [["not met"], "not_fulfilled"],
    [["partially"], "partially_fulfilled"],
    [["met", "fulfilled"], "fulfilled"],
  ];
  return (
    rules.find(([needles]) => needles.some((needle) => text.includes(needle)))?.[1] ??
    "insufficient_information"
  );
}

/** Domain status: the worst status among its criteria. */
export function getDomainStatus(domain: SystemDomain): SystemStatus {
  return domain.items.length ? pickWorstStatus(domain.items.map((item) => item.status)) : "unknown";
}

export type StatusCounts = Record<Exclude<SystemStatus, "unknown">, number>;

function emptyCounts(): StatusCounts {
  return {
    fulfilled: 0,
    almost_fulfilled: 0,
    partially_fulfilled: 0,
    only_limited: 0,
    not_fulfilled: 0,
    insufficient_information: 0,
    zero: 0,
  };
}

/** Criteria per status; criteria without an assessment count as insufficient information. */
export function countStatuses(domains: SystemDomain[]): StatusCounts {
  const counts = emptyCounts();
  for (const domain of domains) {
    for (const item of domain.items) {
      const status = normalizeSystemStatus(item.status);
      counts[status === "unknown" ? "insufficient_information" : status]++;
    }
  }
  return counts;
}

function concreteLocations(analysis: SystemAnalysis): SystemLocation[] {
  return analysis.locations.filter((location) => location.locationId !== ALL_LOCATIONS_ID);
}

/**
 * Summary/legend counts. For "all locations" every location's criteria are
 * counted individually, so the totals add up across locations.
 */
export function getDisplayStatusCounts(
  analysis: SystemAnalysis | null,
  locationId: string,
  domains: SystemDomain[],
): StatusCounts {
  if (locationId !== ALL_LOCATIONS_ID || !analysis) return countStatuses(domains);
  const locations = concreteLocations(analysis);
  if (!locations.length) return countStatuses(domains);
  return countStatuses(
    locations.flatMap((location) => analysis.domainsByLocation[location.locationId] ?? []),
  );
}

/** Per criterion: how many locations reached each status (assessed locations only). */
export function getCriterionStatusCountsByLocation(
  analysis: SystemAnalysis | null,
): Map<string, StatusCounts> {
  const result = new Map<string, StatusCounts>();
  if (!analysis) return result;
  for (const location of concreteLocations(analysis)) {
    for (const domain of analysis.domainsByLocation[location.locationId] ?? []) {
      for (const item of domain.items) {
        const status = normalizeSystemStatus(item.status);
        if (status === "unknown") continue;
        const counts = result.get(item.id) ?? emptyCounts();
        counts[status]++;
        result.set(item.id, counts);
      }
    }
  }
  return result;
}

/** Assessed status of one criterion per location, sorted by location name. */
export function getCriterionStatusesByLocation(
  analysis: SystemAnalysis | null,
  criterionId: string,
  language: string,
): { location: SystemLocation; status: SystemStatus }[] {
  if (!analysis) return [];
  const name = (location: SystemLocation) =>
    (language === "en" ? location.locationName.en : location.locationName.no) ||
    location.locationId;
  return concreteLocations(analysis)
    .flatMap((location) => {
      const item = (analysis.domainsByLocation[location.locationId] ?? [])
        .flatMap((domain) => domain.items)
        .find((entry) => entry.id === criterionId);
      const status = item ? normalizeSystemStatus(item.status) : "unknown";
      return status === "unknown" ? [] : [{ location, status }];
    })
    .sort((a, b) =>
      name(a.location).localeCompare(name(b.location), undefined, {
        sensitivity: "base",
        numeric: true,
      }),
    );
}

// ---------------------------------------------------------------------------
// Annex 4 workbook parsing
// ---------------------------------------------------------------------------

function normalizeHeader(value: string): string {
  return stripDiacritics(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * First non-empty value among `aliases`, matched in alias priority order (not
 * sheet column order) and ignoring case, accents and punctuation.
 */
export function getAliasedValue(row: SourceRow, aliases: string[]): string {
  const entries = Object.entries(row).map(([key, value]) => [normalizeHeader(key), value] as const);
  for (const alias of aliases) {
    const normalized = normalizeHeader(alias);
    for (const [key, value] of entries) {
      if (key === normalized && toText(value)) return toText(value);
    }
  }
  return "";
}

export function normalizeCriterionId(value: unknown): string {
  return toText(value).toUpperCase().replace(/\s+/g, "");
}

const YEAR = ["Year", "Ar", "År"];
const CRITERION_ID = ["Criterion_ID", "Criterion ID", "CriterionId", "ID"];

/** Aliases for a bilingual column: `<base>_NO`, `<base> NO`, `<base>_no`, … */
function bilingual(base: string, lang: "NO" | "EN", extra: string[] = []): string[] {
  const spaced = base.replace(/_/g, " ");
  return [`${base}_${lang}`, `${spaced} ${lang}`, `${base}_${lang.toLowerCase()}`, ...extra];
}

interface CriterionDefinition {
  criterionId: string;
  titleNo: string;
  titleEn: string;
  criterionNo: string;
  criterionEn: string;
  detailNo: string;
  detailEn: string;
  scoreTexts: { no: ScoreTexts; en: ScoreTexts };
}

interface DomainDefinition {
  id: string;
  titleNo: string;
  titleEn: string;
  descriptionNo: string;
  descriptionEn: string;
  criterionIds: string[];
}

interface AssessmentEntry {
  status: SystemStatus;
  assessment: string;
  assessmentTextNo: string;
  assessmentTextEn: string;
  extraSections: ExtraSection[];
}

const GLOBAL_YEAR = "__all__";

/** "a | b" join that skips values already contained in `current`. */
function appendDistinct(current: string, next: string): string {
  if (!next || current.includes(next)) return current;
  return current ? `${current} | ${next}` : next;
}

function mergeExtraSections(existing: ExtraSection[], next: ExtraSection[]): ExtraSection[] {
  const merged = existing.map((section) => ({ ...section }));
  for (const section of next) {
    if (!section.title || !section.text) continue;
    const match = merged.find(
      (s) => s.title === section.title && s.style === section.style && s.lang === section.lang,
    );
    if (match) match.text = appendDistinct(match.text.trim(), section.text.trim());
    else merged.push({ ...section });
  }
  return merged;
}

/** Score label for an assessment such as "3", using the criterion's score texts. */
function getAssessmentLabel(
  rawAssessment: string,
  scoreTexts: { no: ScoreTexts; en: ScoreTexts },
  lang: string,
): string {
  const number = rawAssessment.trim().match(/^(\d+)/)?.[1];
  if (!number) return rawAssessment.trim();
  const preferred = lang === "no" ? scoreTexts.no : scoreTexts.en;
  return preferred[number] || scoreTexts.en[number] || scoreTexts.no[number] || number;
}

function pairWithFallback(no: string, en: string): LocalizedLabel {
  return { no: no || en, en: en || no };
}

/** A criterion without any assessment. */
function blankCriterion(definition: CriterionDefinition): SystemCriterion {
  return {
    id: definition.criterionId,
    titleNo: definition.titleNo,
    titleEn: definition.titleEn,
    criterionNo: definition.criterionNo,
    criterionEn: definition.criterionEn,
    assessmentNo: "",
    assessmentEn: "",
    evidenceNo: definition.detailNo,
    evidenceEn: definition.detailEn,
    status: "unknown",
    statuses: [],
    sources: [],
    extraSections: [],
    scoreTextsNo: definition.scoreTexts.no,
    scoreTextsEn: definition.scoreTexts.en,
    workingCommentNo: definition.detailNo,
    workingCommentEn: definition.detailEn,
    metadata: {},
  };
}

/**
 * Builds system analyses (one per year) from the Annex 4 criterion and data
 * sheets. Criterion rows without a year apply to every year; year-specific
 * rows override them. An `Area_ID` of "ABOUT" supplies the about-panel text.
 */
export function buildSystemAnalyses(
  criterionRows: SourceRow[],
  dataRows: SourceRow[],
): SystemAnalysisDataset {
  if (!criterionRows.length) return { analyses: [] };

  const criteriaByYear = new Map<string, Map<string, CriterionDefinition>>();
  const copiedFromByYear = new Map<string, string>();
  const domainsByYear = new Map<string, Map<string, DomainDefinition>>();
  const aboutTitleByYear = new Map<string, LocalizedLabel>();
  const aboutTextByYear = new Map<string, LocalizedLabel>();

  const mergeAbout = (
    target: Map<string, LocalizedLabel>,
    year: string,
    no: string,
    en: string,
  ) => {
    if (!no && !en) return;
    const current = target.get(year) ?? { no: "", en: "" };
    target.set(year, pairWithFallback(no || current.no, en || current.en));
  };

  for (const row of criterionRows) {
    const year = getAliasedValue(row, YEAR) || GLOBAL_YEAR;
    const copiedFrom = getAliasedValue(row, [CRITERIA_FROM_YEAR_COLUMN]);
    if (copiedFrom && year !== GLOBAL_YEAR) copiedFromByYear.set(year, copiedFrom);
    if (!criteriaByYear.has(year)) criteriaByYear.set(year, new Map());
    if (!domainsByYear.has(year)) domainsByYear.set(year, new Map());
    const criteria = criteriaByYear.get(year)!;
    const domains = domainsByYear.get(year)!;

    const areaIdRaw = getAliasedValue(row, ["Area_ID", "Area ID", "Area", "Domain", "Domain_ID"]);
    const areaTitleNo = getAliasedValue(row, bilingual("Area_title", "NO", ["Area_title"]));
    const areaTitleEn = getAliasedValue(
      row,
      bilingual("Area_title", "EN", ["Area_title", "Area title", "Domain_title_EN"]),
    );
    const areaDescNo = getAliasedValue(
      row,
      bilingual("Area_description", "NO", ["Area_description"]),
    );
    const areaDescEn = getAliasedValue(
      row,
      bilingual("Area_description", "EN", ["Area_description", "Domain_description_EN"]),
    );

    if (areaIdRaw.toUpperCase() === "ABOUT") {
      mergeAbout(aboutTitleByYear, year, areaTitleNo, areaTitleEn);
      mergeAbout(aboutTextByYear, year, areaDescNo, areaDescEn);
    }

    const criterionId = normalizeCriterionId(getAliasedValue(row, CRITERION_ID));
    if (!criterionId) continue;
    const areaId = (areaIdRaw || criterionId.split(".")[0] || "").toUpperCase();

    const existing = domains.get(areaId);
    if (!existing) {
      domains.set(areaId, {
        id: areaId,
        titleNo: areaTitleNo || areaTitleEn || areaId,
        titleEn: areaTitleEn || areaTitleNo || areaId,
        descriptionNo: areaDescNo || areaDescEn,
        descriptionEn: areaDescEn || areaDescNo,
        criterionIds: [criterionId],
      });
    } else {
      if (areaTitleNo) existing.titleNo = areaTitleNo;
      if (areaTitleEn) existing.titleEn = areaTitleEn;
      if (areaDescNo) existing.descriptionNo = areaDescNo;
      if (areaDescEn) existing.descriptionEn = areaDescEn;
      if (!existing.criterionIds.includes(criterionId)) existing.criterionIds.push(criterionId);
    }

    const titleNo = getAliasedValue(row, bilingual("Criterion_title", "NO", ["Criterion_title"]));
    const titleEn = getAliasedValue(
      row,
      bilingual("Criterion_title", "EN", ["Criterion_title", "Criterion title"]),
    );
    const descNo = getAliasedValue(
      row,
      bilingual("Criterion_description", "NO", ["Criterion_description"]),
    );
    const descEn = getAliasedValue(
      row,
      bilingual("Criterion_description", "EN", ["Criterion_description", "Criterion description"]),
    );
    const detailNo = getAliasedValue(
      row,
      bilingual("Criterion_detail", "NO", ["Criterion_detail"]),
    );
    const detailEn = getAliasedValue(
      row,
      bilingual("Criterion_detail", "EN", ["Criterion_detail", "Criterion detail"]),
    );
    const scoreTexts = { no: {} as ScoreTexts, en: {} as ScoreTexts };
    for (const { number } of SYSTEM_SCORE_SCALE) {
      const base = `Criterion_score_${number}`;
      const no = getAliasedValue(row, bilingual(base, "NO"));
      const en = getAliasedValue(row, bilingual(base, "EN", [base, base.replace(/_/g, " ")]));
      scoreTexts.no[number] = no || en;
      scoreTexts.en[number] = en || no;
    }

    criteria.set(criterionId, {
      criterionId,
      titleNo: titleNo || titleEn || descNo || descEn || criterionId,
      titleEn: titleEn || titleNo || descEn || descNo || criterionId,
      criterionNo: descNo || descEn || titleNo || titleEn || criterionId,
      criterionEn: descEn || descNo || titleEn || titleNo || criterionId,
      detailNo: detailNo || detailEn,
      detailEn: detailEn || detailNo,
      scoreTexts,
    });
  }

  const criteriaFor = (year: string) =>
    new Map([...(criteriaByYear.get(GLOBAL_YEAR) ?? []), ...(criteriaByYear.get(year) ?? [])]);

  const domainsFor = (year: string): DomainDefinition[] => {
    const merged = new Map<string, DomainDefinition>();
    for (const source of [domainsByYear.get(GLOBAL_YEAR), domainsByYear.get(year)]) {
      for (const [key, domain] of source ?? []) {
        const current = merged.get(key);
        if (!current) {
          merged.set(key, { ...domain, criterionIds: [...domain.criterionIds] });
          continue;
        }
        if (domain.titleNo) current.titleNo = domain.titleNo;
        if (domain.titleEn) current.titleEn = domain.titleEn;
        if (domain.descriptionNo) current.descriptionNo = domain.descriptionNo;
        if (domain.descriptionEn) current.descriptionEn = domain.descriptionEn;
        current.criterionIds = [...new Set([...current.criterionIds, ...domain.criterionIds])];
      }
    }
    return [...merged.values()].sort((a, b) => compareNatural(a.id, b.id));
  };

  const entries = new Map<string, AssessmentEntry>();
  const locationsByYear = new Map<string, Set<string>>();
  const entryKey = (year: string, location: string, criterionId: string) =>
    `${year}||${location}||${criterionId}`;

  for (const row of dataRows) {
    const location = getAliasedValue(row, ["Location", "Region", "Lokasjon", "Sted"]);
    const year = getAliasedValue(row, YEAR);
    const criterionId = normalizeCriterionId(getAliasedValue(row, CRITERION_ID));
    if (!location || !year || !criterionId || !criteriaFor(year).has(criterionId)) continue;

    const textAliases = ["Assessment_text", "Assessment text", "Details", "Comment", "Kommentar"];
    const assessment = getAliasedValue(row, [
      "Criterion_assessment",
      "Criterion assessment",
      "Assessment",
      "Score",
      "Status",
    ]);
    const assessmentTextNo = getAliasedValue(row, bilingual("Assessment_text", "NO", textAliases));
    const assessmentTextEn = getAliasedValue(row, bilingual("Assessment_text", "EN", textAliases));
    const extraSections = parseExtraSections(row);

    if (!locationsByYear.has(year)) locationsByYear.set(year, new Set());
    locationsByYear.get(year)!.add(location);

    const key = entryKey(year, location, criterionId);
    const status = mapAssessmentToStatus(assessment);
    const existing = entries.get(key);
    if (!existing) {
      entries.set(key, { status, assessment, assessmentTextNo, assessmentTextEn, extraSections });
      continue;
    }
    existing.status = pickWorstStatus([existing.status, status]);
    existing.assessment = appendDistinct(existing.assessment, assessment);
    existing.assessmentTextNo = appendDistinct(existing.assessmentTextNo, assessmentTextNo);
    existing.assessmentTextEn = appendDistinct(existing.assessmentTextEn, assessmentTextEn);
    existing.extraSections = mergeExtraSections(existing.extraSections, extraSections);
  }

  const years = [...locationsByYear.keys()].sort((a, b) => {
    const [na, nb] = [Number(a), Number(b)];
    return !Number.isNaN(na) && !Number.isNaN(nb)
      ? nb - na
      : b.localeCompare(a, undefined, { numeric: true });
  });

  const analyses = years.map((year): SystemAnalysis => {
    const criteria = criteriaFor(year);
    const domainOrder = domainsFor(year);
    const locations = [...(locationsByYear.get(year) ?? [])].sort((a, b) => a.localeCompare(b));
    const locationEntries: SystemLocation[] = [
      {
        locationId: ALL_LOCATIONS_ID,
        locationName: { no: "Alle lokasjoner", en: "All locations" },
      },
      ...locations.map((location) => ({
        locationId: location,
        locationName: { no: location, en: location },
      })),
    ];

    const buildCriterion = (
      definition: CriterionDefinition,
      locationId: string,
    ): SystemCriterion => {
      const isAll = locationId === ALL_LOCATIONS_ID;
      let status: SystemStatus = "unknown";
      let statuses: SystemStatus[] = [];
      let assessmentNo = "";
      let assessmentEn = "";
      let textNo = "";
      let textEn = "";
      let extraSections: ExtraSection[] = [];

      if (isAll) {
        const found = locations
          .map((location) => entries.get(entryKey(year, location, definition.criterionId)))
          .filter((entry): entry is AssessmentEntry => !!entry);
        if (found.length) {
          const unique = (values: string[]) => [...new Set(values.filter(Boolean))].join(" | ");
          status = pickWorstStatus(found.map((entry) => entry.status));
          statuses = [...new Set(found.map((entry) => normalizeSystemStatus(entry.status)))];
          assessmentNo = unique(
            found.map((e) => getAssessmentLabel(e.assessment, definition.scoreTexts, "no")),
          );
          assessmentEn = unique(
            found.map((e) => getAssessmentLabel(e.assessment, definition.scoreTexts, "en")),
          );
          textNo = unique(found.map((e) => e.assessmentTextNo));
          textEn = unique(found.map((e) => e.assessmentTextEn));
          extraSections = found.reduce<ExtraSection[]>(
            (acc, e) => mergeExtraSections(acc, e.extraSections),
            [],
          );
        }
      } else {
        const entry = entries.get(entryKey(year, locationId, definition.criterionId));
        if (entry) {
          status = normalizeSystemStatus(entry.status);
          statuses = [status];
          assessmentNo = getAssessmentLabel(entry.assessment, definition.scoreTexts, "no");
          assessmentEn = getAssessmentLabel(entry.assessment, definition.scoreTexts, "en");
          textNo = entry.assessmentTextNo;
          textEn = entry.assessmentTextEn;
          extraSections = entry.extraSections;
        }
      }

      return {
        ...blankCriterion(definition),
        assessmentNo,
        assessmentEn,
        evidenceNo: textNo || definition.detailNo,
        evidenceEn: textEn || definition.detailEn,
        status,
        statuses,
        extraSections,
      };
    };

    const domainsByLocation = Object.fromEntries(
      locationEntries.map(({ locationId }) => [
        locationId,
        domainOrder.map((domain) => ({
          id: domain.id,
          titleNo: domain.titleNo || domain.id,
          titleEn: domain.titleEn || domain.id,
          descriptionNo: domain.descriptionNo,
          descriptionEn: domain.descriptionEn,
          items: domain.criterionIds
            .map((id) => criteria.get(id))
            .filter((definition): definition is CriterionDefinition => !!definition)
            .map((definition) => buildCriterion(definition, locationId))
            .sort((a, b) => compareNatural(a.id, b.id)),
        })),
      ]),
    );

    const aboutTitle = aboutTitleByYear.get(year) ??
      aboutTitleByYear.get(GLOBAL_YEAR) ?? { no: "", en: "" };
    const aboutText = aboutTextByYear.get(year) ??
      aboutTextByYear.get(GLOBAL_YEAR) ?? { no: "", en: "" };
    return {
      analysisId: `annex4_${year}`,
      analysisName: { no: `Systemanalyse ${year}`, en: `System analysis ${year}` },
      year: Number(year) || year,
      aboutTitle: pairWithFallback(aboutTitle.no, aboutTitle.en),
      aboutText: pairWithFallback(aboutText.no, aboutText.en),
      locations: locationEntries,
      domainsByLocation,
    };
  });

  const ownYears = [...criteriaByYear.keys()].filter(
    (year) => year !== GLOBAL_YEAR && !copiedFromByYear.has(year),
  );
  const criteriaSets = ownYears
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
    .map((year) => ({
      year,
      criteria: [...criteriaFor(year).values()]
        .map(blankCriterion)
        .sort((a, b) => compareNatural(a.id, b.id)),
    }));
  const criteriaSourceByYear = Object.fromEntries(
    [...criteriaByYear.keys()]
      .filter((year) => year !== GLOBAL_YEAR)
      .map((year) => [year, copiedFromByYear.get(year) ?? year]),
  );

  return { analyses, criteriaSets, criteriaSourceByYear };
}

/**
 * Attaches per-location audit info and raw assessment rows (used by the
 * edit dialog) to analyses built from database rows.
 */
export function attachAssessmentRecords(
  dataset: SystemAnalysisDataset,
  records: {
    row: SourceRow;
    year: string;
    criterionId: string;
    location: string;
    updatedAt: string | null;
    updatedBy: string | null;
  }[],
): SystemAnalysisDataset {
  return {
    ...dataset,
    analyses: dataset.analyses.map((analysis) => {
      const auditByLocation: Record<string, SystemAnalysisAudit> = {};
      const assessmentsByLocation: Record<string, SystemAssessmentRow[]> = {};
      for (const record of records) {
        if (String(record.year) !== String(analysis.year) || !record.location) continue;
        (assessmentsByLocation[record.location] ??= []).push({
          criterion_id: record.criterionId,
          row_data: record.row,
        });
        const current = auditByLocation[record.location];
        if (record.updatedAt && (!current || record.updatedAt > current.updatedAt)) {
          auditByLocation[record.location] = {
            updatedAt: record.updatedAt,
            updatedBy: record.updatedBy,
          };
        }
      }
      return { ...analysis, auditByLocation, assessmentsByLocation };
    }),
  };
}

/**
 * The bundled sample only provides the domain/criterion skeleton. Every
 * criterion is shown as "insufficient information" until real data loads.
 */
export function createPlaceholderDataset(
  sample: SystemAnalysisDataset,
  insufficientText: { no: string; en: string },
): SystemAnalysisDataset {
  return {
    analyses: sample.analyses.map((analysis) => ({
      ...analysis,
      domainsByLocation: Object.fromEntries(
        Object.entries(analysis.domainsByLocation).map(([locationId, domains]) => [
          locationId,
          domains.map((domain) => ({
            ...domain,
            items: domain.items.map((item) => ({
              ...item,
              status: "insufficient_information" as const,
              assessmentNo: insufficientText.no,
              assessmentEn: insufficientText.en,
              evidenceNo: insufficientText.no,
              evidenceEn: insufficientText.en,
              workingCommentNo: "",
              workingCommentEn: "",
              sources: [],
            })),
          })),
        ]),
      ),
    })),
  };
}
